import {
  Component,
  type ComponentType,
  type ErrorInfo,
  lazy,
  type ReactNode,
  Suspense,
  useMemo,
  useState,
} from "react";
import { reportClientError } from "../core/telemetry";
import { ErrorState, LoadingState } from "../shared/Shared";

/**
 * Route-level code splitting for the member surfaces (builder, console, wizard,
 * history, billing, admin): the public pages never download them. Unlike a bare
 * `React.lazy`, a dropped chunk does not reach the root boundary and is not cached as
 * failed forever — the route shows the ordinary error state and Retry re-imports.
 */
export function lazyRoute<P extends object>(
  load: () => Promise<ComponentType<P>>,
): ComponentType<P> {
  return function LazyRoute(props: P) {
    const [attempt, setAttempt] = useState(0);
    // A fresh lazy component per attempt: React caches a rejected import per instance.
    // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` IS the dependency — it forces a new instance.
    const Chunk = useMemo(
      () => lazy(() => load().then((component) => ({ default: component }))),
      [attempt],
    );
    return (
      <ChunkBoundary key={attempt} onRetry={() => setAttempt((value) => value + 1)}>
        <Suspense fallback={<LoadingState />}>
          <Chunk {...props} />
        </Suspense>
      </ChunkBoundary>
    );
  };
}

class ChunkBoundary extends Component<
  { children: ReactNode; onRetry: () => void },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    reportClientError({
      kind: "react-boundary",
      message: error instanceof Error ? error.message : String(error ?? "route chunk failure"),
      stack: error instanceof Error ? (error.stack ?? undefined) : undefined,
      componentStack: info.componentStack ?? undefined,
    });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <ErrorState
        titleId="error.chunkTitle"
        bodyId="error.chunkBody"
        onRetry={() => {
          this.setState({ failed: false });
          this.props.onRetry();
        }}
      />
    );
  }
}
